import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { Image } from "./Image";
import { LiquidGlass } from "./LiquidGlass";
import { SelectionIndicator } from "./SelectionIndicator";
import "./Motion.css";
import "./CommandMenu.css";

export type CommandItem =
  | {
      kind?: "action";
      id: string;
      label: ReactNode;
      icon?: ReactNode;
      disabled?: boolean;
      danger?: boolean;
      selected?: boolean;
      className?: string;
    }
  | { kind: "section"; id: string; label: ReactNode; items: CommandItem[] }
  | { kind: "separator"; id: string };

/** A command surface in the browser's top layer, including inside dialogs. */
export function CommandMenu({
  trigger,
  items,
  onSelect,
  label = "操作菜单",
  align = "end",
  className = "",
}: {
  trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
  items: CommandItem[];
  onSelect: (id: string) => void;
  label?: string;
  align?: "start" | "end";
  className?: string;
}) {
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const menu = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const focusLast = useRef(false);
  const position = useCallback(() => {
    const surface = menu.current;
    const button = anchor.current?.querySelector("button");
    if (!surface || !button || !surface.matches(":popover-open")) return;
    const rect = button.getBoundingClientRect();
    const left = align === "end" ? rect.right - surface.offsetWidth : rect.left;
    const below = window.innerHeight - rect.bottom - 12;
    const top =
      surface.offsetHeight > below && rect.top > below
        ? rect.top - surface.offsetHeight - 6
        : rect.bottom + 6;
    surface.style.left = `${Math.max(12, Math.min(left, window.innerWidth - surface.offsetWidth - 12))}px`;
    surface.style.top = `${Math.max(12, Math.min(top, window.innerHeight - surface.offsetHeight - 12))}px`;
  }, [align]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, position]);

  function commands() {
    return Array.from(
      menu.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled)',
      ) ?? [],
    );
  }
  function close(restoreFocus: boolean) {
    if (menu.current?.matches(":popover-open")) menu.current.hidePopover();
    if (restoreFocus) anchor.current?.querySelector("button")?.focus();
  }
  function renderItems(entries: CommandItem[]): ReactNode {
    return entries.map((item) => {
      if (item.kind === "separator")
        return (
          <hr key={item.id} role="separator" className="ark-command-divider" />
        );
      if (item.kind === "section")
        return (
          <div key={item.id} role="group" aria-labelledby={`${id}-${item.id}`}>
            <div id={`${id}-${item.id}`} className="ark-command-section">
              {item.label}
            </div>
            {renderItems(item.items)}
          </div>
        );
      return (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          tabIndex={-1}
          disabled={item.disabled}
          data-danger={item.danger || undefined}
          data-selected={item.selected || undefined}
          data-ark-selection-item=""
          className={`ark-command-item ${item.className ?? ""}`}
          onClick={() => {
            close(true);
            onSelect(item.id);
          }}
        >
          {item.icon ? (
            <span className="ark-command-icon">{item.icon}</span>
          ) : null}
          {item.label}
        </button>
      );
    });
  }

  return (
    <span
      ref={anchor}
      className="ark-command-anchor"
      onKeyDown={(event) => {
        if (
          event.defaultPrevented ||
          menu.current?.contains(event.target as Node)
        )
          return;
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          focusLast.current = event.key === "ArrowUp";
          menu.current?.showPopover();
        }
      }}
    >
      {cloneElement(trigger, {
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": id,
        popoverTarget: id,
        popoverTargetAction: "toggle",
      })}
      <LiquidGlass
        ref={menu}
        id={id}
        popover="auto"
        role="menu"
        aria-label={label}
        inert={!open}
        className={`ark-command-menu ${className}`}
        onBeforeToggle={(event) => {
          const next = event.newState === "open";
          // Exit remains visible briefly, but immediately leaves the focus order.
          event.currentTarget.inert = !next;
          setOpen(next);
        }}
        onToggle={(event) => {
          const next = event.newState === "open";
          setOpen(next);
          if (next) {
            position();
            const buttons = commands();
            (focusLast.current ? buttons.at(-1) : buttons[0])?.focus();
            focusLast.current = false;
          }
        }}
        onBlur={(event) => {
          if (
            event.relatedTarget instanceof Node &&
            !event.currentTarget.contains(event.relatedTarget)
          )
            close(false);
        }}
        onKeyDown={(event) => {
          const buttons = commands();
          const index = buttons.findIndex(
            (button) => button === document.activeElement,
          );
          let next: number | undefined;
          if (event.key === "ArrowDown") next = (index + 1) % buttons.length;
          else if (event.key === "ArrowUp")
            next = (index - 1 + buttons.length) % buttons.length;
          else if (event.key === "Home") next = 0;
          else if (event.key === "End") next = buttons.length - 1;
          else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            close(true);
          }
          if (next !== undefined) {
            event.preventDefault();
            buttons[next]?.focus();
          }
        }}
      >
        <SelectionIndicator
          rootRef={menu}
          selector="[data-selected]"
          followInteraction
        />
        {renderItems(items)}
      </LiquidGlass>
    </span>
  );
}

export function Avatar({
  src,
  size = 32,
  shape = "circle",
  className = "",
  children,
  style,
  ...props
}: Omit<HTMLAttributes<HTMLSpanElement>, "children"> & {
  src?: string | undefined;
  size?: number;
  shape?: "circle" | "square";
  children?: ReactNode;
}) {
  return (
    <span
      {...props}
      className={`ark-avatar ${className}`}
      data-shape={shape}
      style={{ "--ark-avatar-size": `${size}px`, ...style } as CSSProperties}
    >
      <Image
        src={src}
        alt=""
        fallback={<span aria-hidden="true">{children}</span>}
      />
    </span>
  );
}
